import { useState, useRef, useEffect, useCallback } from 'react';
import { AUDIO } from '../constants';

interface UseAudioPlayerOptions {
  autoPlay?: boolean;
  onEnded?: () => void;
  onError?: (error: string) => void;
}

interface AudioPlayerState {
  isPlaying: boolean;
  isLoading: boolean;
  currentTime: number;
  duration: number;
  error: string | null;
  hasStopped: boolean;
  hasStartedPlaying: boolean;
}

interface AudioPlayerActions {
  play: () => Promise<void>;
  pause: () => void;
  stop: () => void;
  seek: (time: number) => void;
  setPlaybackRate: (rate: number) => void;
  reset: () => void;
}

export function useAudioPlayer(
  audioSrc: string | null,
  options: UseAudioPlayerOptions = {}
): AudioPlayerState & AudioPlayerActions {
  const { autoPlay = false, onEnded, onError } = options;
  
  // Store callbacks in refs to prevent re-renders
  const onEndedRef = useRef(onEnded);
  const onErrorRef = useRef(onError);
  
  // Update refs when callbacks change
  useEffect(() => {
    onEndedRef.current = onEnded;
  }, [onEnded]);
  
  useEffect(() => {
    onErrorRef.current = onError;
  }, [onError]);
  
  // Stable callbacks that don't change
  const stableOnEnded = useCallback(() => {
    if (onEndedRef.current) onEndedRef.current();
  }, []);
  
  const stableOnError = useCallback((error: string) => {
    if (onErrorRef.current) onErrorRef.current(error);
  }, []);

  // State
  const [isPlaying, setIsPlaying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasStopped, setHasStopped] = useState(false);
  const [hasStartedPlaying, setHasStartedPlaying] = useState(false);

  // Refs
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const hasStoppedRef = useRef(false);
  const isSeekingRef = useRef(false);

  // Reset all state
  const reset = useCallback(() => {
    setIsPlaying(false);
    setIsLoading(false);
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    setHasStopped(false);
    setHasStartedPlaying(false);
    hasStoppedRef.current = false;
    isSeekingRef.current = false;
  }, []);

  // Initialize audio element when src changes
  useEffect(() => {
    if (!audioSrc) {
      reset();
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      return;
    }

    // Clean up any existing audio element first
    if (audioRef.current) {
      const oldAudio = audioRef.current;
      // Stop and clean up the old audio element immediately
      oldAudio.pause();
      oldAudio.currentTime = 0;
      oldAudio.src = '';
      oldAudio.load();
      audioRef.current = null;
    }

    // Reset state for new audio
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
    setError(null);
    setHasStopped(false);
    setHasStartedPlaying(false);
    hasStoppedRef.current = false;
    isSeekingRef.current = false;
    setIsLoading(true);

    // Create new audio element
    const audio = new Audio(audioSrc);
    audio.preload = AUDIO.PRELOAD_MODE;
    audioRef.current = audio;

    // Event handlers
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoading(false);
    };

    const handleTimeUpdate = () => {
      setCurrentTime(audio.currentTime);
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      stableOnEnded();
    };

    const handleError = (event: Event) => {
      const errorMessage = `Failed to load audio: ${audioSrc}`;
      console.error('Audio loading error:', errorMessage, event);
      setError(errorMessage);
      setIsLoading(false);
      setIsPlaying(false);
      stableOnError(errorMessage);
    };

    const handleCanPlay = () => {
      setError(null);
      setIsLoading(false);

      // Auto-play if enabled and not stopped or seeking
      if (autoPlay && !hasStoppedRef.current && !isSeekingRef.current) {
        handleAutoPlay();
      }
    };

    const handleAutoPlay = async () => {
      try {
        await audio.play();
        setIsPlaying(true);
        setHasStartedPlaying(true);
      } catch (err) {
        setIsPlaying(false);
      }
    };

    // Add event listeners
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('error', handleError);
    audio.addEventListener('canplay', handleCanPlay);
    
    // Trigger loading
    audio.load();

    // Cleanup function
    return () => {
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('error', handleError);
      audio.removeEventListener('canplay', handleCanPlay);
      
      audio.pause();
      audio.currentTime = 0;
      audio.src = '';
      audio.load();
    };
  }, [audioSrc, autoPlay]);

  // Actions
  const play = useCallback(async () => {
    const audio = audioRef.current;
    if (!audio || !audioSrc) return;

    try {
      setIsLoading(true);
      setError(null);

      if (isPlaying) {
        audio.pause();
        setIsPlaying(false);
      } else {
        // If stopped, restart from beginning
        if (hasStopped) {
          audio.currentTime = 0;
          setCurrentTime(0);
          setHasStopped(false);
          hasStoppedRef.current = false;
        }
        
        await audio.play();
        setIsPlaying(true);
        setHasStartedPlaying(true);
      }
    } catch (err) {
      const errorMessage = 'Failed to play audio';
      setError(errorMessage);
      setIsPlaying(false);
      stableOnError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [audioSrc, isPlaying, hasStopped]);

  const pause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.pause();
    setIsPlaying(false);
  }, []);

  const stop = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) return;

    // Set refs immediately to prevent race conditions
    hasStoppedRef.current = true;
    setHasStopped(true);
    setHasStartedPlaying(false);

    audio.pause();
    audio.currentTime = 0;
    setIsPlaying(false);
    setCurrentTime(0);
  }, []);

  const seek = useCallback((time: number) => {
    const audio = audioRef.current;
    if (!audio || duration === 0) return;

    // Set seeking flag to prevent auto-play during seek
    isSeekingRef.current = true;
    
    const clampedTime = Math.max(0, Math.min(time, duration));
    audio.currentTime = clampedTime;
    setCurrentTime(clampedTime);

    // Clear seeking flag after delay
    setTimeout(() => {
      isSeekingRef.current = false;
    }, AUDIO.SEEK_TIMEOUT);
  }, [duration]);

  const setPlaybackRate = useCallback((rate: number) => {
    const audio = audioRef.current;
    if (!audio) return;

    audio.playbackRate = rate;
  }, []);

  return {
    // State
    isPlaying,
    isLoading,
    currentTime,
    duration,
    error,
    hasStopped,
    hasStartedPlaying,
    // Actions
    play,
    pause,
    stop,
    seek,
    setPlaybackRate,
    reset,
  };
}